import React, { useState, useMemo, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import cities from "../data/cities.json";

const ValidationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { errors = [], formData = {} } = location.state || {};

  const [activeTab, setActiveTab] = useState("findFlights");
  const [activeService, setActiveService] = useState("flight");
  const [tripType, setTripType] = useState(formData.tripType || "roundTrip");
  const [redeemMiles, setRedeemMiles] = useState(formData.redeemMiles || false);
  const [adults, setAdults] = useState(formData.adults || 1);
  const [children, setChildren] = useState(formData.children || 0);
  const [fromQuery, setFromQuery] = useState("");
  const [toQuery, setToQuery] = useState(formData.toQuery || "");
  const [departDate, setDepartDate] = useState(formData.departDate || null);
  const [returnDate, setReturnDate] = useState(formData.returnDate || null);
  const [departInput, setDepartInput] = useState(formData.departInput || "");
  const [returnInput, setReturnInput] = useState(formData.returnInput || "");
  const [farePreference, setFarePreference] = useState(formData.farePreference || "coach");
  const [showConnecting, setShowConnecting] = useState(false);
  const [connectingQuery, setConnectingQuery] = useState("");
  const [isConnectingDropdownOpen, setIsConnectingDropdownOpen] = useState(false);
  
  // Multi-city flights state
  const [multiCityFlights, setMultiCityFlights] = useState([
    { id: 1, from: "", to: "", departDate: null, departInput: "", includeNearbyFrom: false, includeNearbyTo: false },
    { id: 2, from: "", to: "", departDate: null, departInput: "", includeNearbyFrom: false, includeNearbyTo: false }
  ]);

  // Trip type dropdown (custom UI)
  const [isTripMenuOpen, setIsTripMenuOpen] = useState(false);
  const tripDropdownRef = useRef(null);
  const tripOptions = [
    { value: "roundTrip", label: "Round trip" },
    { value: "oneWay", label: "One way" },
    { value: "multiCity", label: "Multi city" }
  ];
  
  // Recent search dropdown (custom UI)
  const [isRecentMenuOpen, setIsRecentMenuOpen] = useState(false);
  const recentDropdownRef = useRef(null);
  
  // Date picker modal
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const [activeDateField, setActiveDateField] = useState(null); // 'depart' or 'return'
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const datePickerRef = useRef(null);
  
  // Dropdown visibility
  const [isFromDropdownOpen, setIsFromDropdownOpen] = useState(false);
  const [isToDropdownOpen, setIsToDropdownOpen] = useState(false);
  const fromDropdownRef = useRef(null);
  const toDropdownRef = useRef(null);
  const connectingDropdownRef = useRef(null);
  
  useEffect(() => {
    const onDocClick = (e) => {
      if (!tripDropdownRef.current) return;
      if (!tripDropdownRef.current.contains(e.target)) setIsTripMenuOpen(false);
      
      if (!recentDropdownRef.current) return;
      if (!recentDropdownRef.current.contains(e.target)) setIsRecentMenuOpen(false);
      
      // From dropdown outside click
      if (fromDropdownRef.current && !fromDropdownRef.current.contains(e.target)) {
        setIsFromDropdownOpen(false);
      }
      
      // To dropdown outside click
      if (toDropdownRef.current && !toDropdownRef.current.contains(e.target)) {
        setIsToDropdownOpen(false);
      }
      
      // Connecting dropdown outside click
      if (connectingDropdownRef.current && !connectingDropdownRef.current.contains(e.target)) {
        setIsConnectingDropdownOpen(false);
      }
      
      // Date picker modal should only close via Done button, not on outside clicks
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);
  
  const openDatePicker = (field) => {
    // Always start with departure field selection when opening
    setActiveDateField('depart');
    setIsDatePickerOpen(true);
  };
  
  const formatDate = (date) => {
    if (!date) return '';
    const options = { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' };
    return date.toLocaleDateString('en-US', options);
  };
  
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    // Add empty days for padding
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    return days;
  };

  const getNextMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 1);
  };
  
  const isSameDay = (date1, date2) => {
    if (!date1 || !date2) return false;
    return date1.toDateString() === date2.toDateString();
  };
  
  const isInRange = (date, start, end) => {
    if (!date || !start || !end) return false;
    return date >= start && date <= end;
  };
  
  const handleDateSelect = (date) => {
    if (tripType === "oneWay" || tripType === "multiCity") {
      // For one-way and multi-city, only set departure date
      setDepartDate(date);
      setDepartInput(formatDate(date));
    } else {
      // Round trip logic (existing)
      if (!departDate) {
        // First click - always set as departure date
        setDepartDate(date);
        setDepartInput(formatDate(date));
        setActiveDateField('return'); // Auto-switch to return selection
      } else if (!returnDate) {
        // Second click - set as return date if it's after departure
        if (date >= departDate) {
          setReturnDate(date);
          setReturnInput(formatDate(date));
        } else {
          // If clicked date is before departure, reset and make it the new departure
          setDepartDate(date);
          setDepartInput(formatDate(date));
          setReturnDate(null);
          setReturnInput('');
          setActiveDateField('return');
        }
      } else {
        // Both dates are selected, start over with new departure date
        setDepartDate(date);
        setDepartInput(formatDate(date));
        setReturnDate(null);
        setReturnInput('');
        setActiveDateField('return');
      }
    }
  };
  
  const clearDates = () => {
    setDepartDate(null);
    setReturnDate(null);
    setDepartInput('');
    setReturnInput('');
    setActiveDateField('depart'); // Reset to departure selection
  };
  
  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
  };
  
  const prevMonth = () => {
    const today = new Date();
    const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const todayMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    
    // Only allow going back if current displayed month is after today's month
    if (currentMonthStart > todayMonthStart) {
      setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
    }
  };

  const isPrevMonthDisabled = () => {
    const today = new Date();
    const currentMonthStart = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const todayMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    return currentMonthStart <= todayMonthStart;
  };
  const selectedTripLabel = (tripOptions.find((o) => o.value === tripType) || tripOptions[0]).label;

  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };

  const handleServiceChange = (service) => {
    setActiveService(service);
  };

  const handleSwapAirports = () => {
    const tempFrom = fromQuery;
    setFromQuery(toQuery);
    setToQuery(tempFrom);
  };

  const handleSearch = () => {
    navigate('/search-results');
  };

  // Multi-city functions
  const addFlight = () => {
    const newFlight = {
      id: multiCityFlights.length + 1,
      from: "",
      to: "",
      departDate: null,
      departInput: "",
      includeNearbyFrom: false,
      includeNearbyTo: false
    };
    setMultiCityFlights([...multiCityFlights, newFlight]);
  };

  const updateMultiCityFlight = (flightId, field, value) => {
    setMultiCityFlights(flights =>
      flights.map(flight =>
        flight.id === flightId ? { ...flight, [field]: value } : flight
      )
    );
  };

  const removeFlight = (flightId) => {
    if (multiCityFlights.length > 2) {
      setMultiCityFlights(flights => flights.filter(flight => flight.id !== flightId));
    }
  };

  return (
    <>
      <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-[38px] font-light text-[#0078D2] mb-4" style={{ fontFamily: 'AmericanSansLight, Helvetica, Arial', margin: '0px 0px 15px' }}>Book flights</h1>
      </div>

      {/* Error Message Box */}
      <div className="mb-6 border-l-4 border-red-500 border border-red-500 bg-white p-4 rounded-sm">
        <div className="flex">
          <div className="flex-shrink-0 mr-3">
            <div 
              className="w-8 h-8"
              style={{
                backgroundImage: 'url(/content/images/notification-icons.gif)',
                backgroundPosition: '0 0',
                backgroundSize: '64px 64px',
                backgroundRepeat: 'no-repeat'
              }}
              aria-label="Error icon"
            ></div>
          </div>
          <div className="flex-1">
            <h2 className="text-base font-semibold mb-2 text-gray-900">Something went wrong</h2>
            <p className="mb-3 text-gray-700">Please fix the {errors.length} error{errors.length !== 1 ? 's' : ''}.</p>
            <ul className="space-y-1">
              {errors.map((error, index) => (
                <li key={index} className="flex items-center">
                  <span className="w-1.5 h-1.5 bg-black rounded-full mr-2"></span>
                  <span className="text-blue-600 underline cursor-pointer hover:no-underline text-sm" style={{ textDecorationColor: 'black' }}>
                    {error}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Required Fields Note */}
      <div className="mb-6">
        <small className="text-sm text-gray-600">
          (<span className="text-red-500">•</span> Required)
        </small>
      </div>

      {/* Divider */}
      <div className="my-8 border-t border-gray-200"></div>

      {/* Cities and dates section */}
      <div className="mt-8 max-w-4xl">
        <h2 className="text-[30px] font-light text-[#00467F] mb-8" style={{ fontFamily: 'AmericanSansLight, Helvetica, Arial' }}>Cities and dates</h2>
        
        <div className="flex items-center mb-6">
          <div className={`relative ${isTripMenuOpen ? "border-2 border-[#0078d2] rounded-[4px] p-[2px]" : ""}`} ref={tripDropdownRef}>
            <button
              type="button"
              onClick={() => setIsTripMenuOpen((o) => !o)}
              className="appearance-none hover:bg-[#e6f3ff] border border-black pl-12 pr-8 py-3 text-[#0078d2] text-base min-w-[280px] h-12 text-left rounded-[4px]"
            >
              {selectedTripLabel}
            </button>
            <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
              <div className="w-6 h-6 rounded-full border-2 border-[#0078d2] flex items-center justify-center mr-2">
                <svg className="w-3 h-3 text-[#0078d2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"></path>
                </svg>
              </div>
            </div>
            <svg className={`w-4 h-4 text-[#0078d2] absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none ${isTripMenuOpen ? "rotate-180" : ""}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
            </svg>

            {isTripMenuOpen && (
              <div className="absolute z-20 mt-0 w-full bg-white rounded-b-lg shadow-lg pt-[4px]">
                {tripOptions.map((opt) => {
                  const selected = tripType === opt.value;
                  return (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => {
                        setTripType(opt.value);
                        setIsTripMenuOpen(false);
                      }}
                      className={`flex w-full items-center justify-between px-4 py-3 text-left ${selected ? "bg-[#eaf3ff] border border-[#0078d2]" : "hover:bg-[#f5f9ff]"}`}
                    >
                      <span className="text-gray-900 text-base">{opt.label}</span>
                      {selected && (
                        <svg className="w-4 h-4 text-[#0078d2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          {!redeemMiles && (
            <a href="/vacation-packages" className="text-[#0078d2] hover:underline ml-8 flex items-center text-base">
              Vacation packages 
              <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
              </svg>
            </a>
          )}
        </div>

        <div className="mb-6">
          <p className="text-[#50657C] text-[13px] mb-2" style={{ fontFamily: '"AmericanSans Var", Helvetica, Arial', margin: '0px 0px 8px' }}>Do you want to use a recent search?</p>
          <div className="relative inline-block min-w-[280px] focus-within:border-2 focus-within:border-[#0078d2] focus-within:rounded-[4px] focus-within:p-[2px]">
            <select
              id="recent-searches"
              name="recent-searches"
              className="appearance-none bg-white border border-[#0078d2] text-[#374151] text-[14px] w-full rounded-[4px] focus:outline-none h-[46px] pr-8"
              style={{ 
                fontFamily: '-apple-system, "system-ui", "Segoe UI", Roboto, Arial, sans-serif',
                padding: '12px 40px 12px 16px'
              }}
            >
              <option value="">Recent searches</option>
              <option value="LAX to SFO Aug 21">LAX to SFO Aug 21</option>
              <option value="SYX to SBA Aug 21 - Aug 29">SYX to SBA Aug 21 - Aug 29</option>
              <option value="AMD to SBA Aug 21 - Aug 29">AMD to SBA Aug 21 - Aug 29</option>
              <option value="IGM to SBP Aug 21 - Aug 27">IGM to SBP Aug 21 - Aug 27</option>
              <option value="SJC to LAX Aug 21 - Aug 29">SJC to LAX Aug 21 - Aug 29</option>
              <option value="clear">Clear history</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
              <svg className="w-4 h-4 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd"></path>
              </svg>
            </div>
          </div>
        </div>

        {/* Multi-city vs Regular Form */}
        {tripType === "multiCity" ? (
          /* Multi-city Form */
          <div className="mb-2">
            {multiCityFlights.map((flight, index) => (
              <div key={flight.id} className="mb-2">
                <div className="flex items-center mb-2">
                  <h3 
                    className="font-light" 
                    style={{ 
                      fontSize: '24px', 
                      fontFamily: 'AmericanSansLight, Helvetica, Arial', 
                      color: '#627A88',
                      margin: '0px 0px 5px'
                    }}
                  >
                    Flight {index + 1}
                  </h3>
                  {index > 1 && (
                    <button
                      type="button"
                      onClick={() => removeFlight(flight.id)}
                      className="text-[#0078d2] underline hover:underline text-sm ml-4"
                    >
                      Delete flight
                    </button>
                  )}
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  {/* From */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      From <span className="text-red-500">•</span>
                    </label>
                    <div className="relative max-w-[280px] focus-within:border-2 focus-within:border-[#0078d2] focus-within:p-[2px]">
                      <input
                        type="text"
                        value={flight.from}
                        onChange={(e) => updateMultiCityFlight(flight.id, 'from', e.target.value)}
                        placeholder="Leaving from"
                        className="w-full h-12 px-4 pr-12 text-base focus:outline-none border border-gray-600"
                      />
                      <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                        <img 
                          src="/arrow.png" 
                          alt="Arrow icon" 
                          className="w-8 h-8 brightness-50"
                        />
                      </div>
                      
                      {/* From dropdown */}
                      {flight.from && (
                        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-auto">
                          {cities
                            .filter(city => 
                              city.code.toLowerCase().includes(flight.from.toLowerCase()) ||
                              city.city.toLowerCase().includes(flight.from.toLowerCase()) ||
                              city.airport.toLowerCase().includes(flight.from.toLowerCase()) ||
                              city.keywords.some(keyword => keyword.toLowerCase().includes(flight.from.toLowerCase()))
                            )
                            .slice(0, 8)
                            .map((city) => (
                              <button
                                key={city.code}
                                type="button"
                                onClick={() => updateMultiCityFlight(flight.id, 'from', city.code)}
                                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50"
                              >
                                <div className="font-medium">{city.code} - {city.airport}</div>
                                <div className="text-sm text-gray-500">{city.city}{city.state ? `, ${city.state}` : ''}, {city.country}</div>
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* To */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      To <span className="text-red-500">•</span>
                    </label>
                    <div className="relative max-w-[280px] focus-within:border-2 focus-within:border-[#0078d2] focus-within:p-[2px]">
                      <input
                        type="text"
                        value={flight.to}
                        onChange={(e) => updateMultiCityFlight(flight.id, 'to', e.target.value)}
                        placeholder="Going to"
                        className="w-full h-12 px-4 text-base focus:outline-none border border-gray-600"
                      />
                      
                      {/* To dropdown */}
                      {flight.to && (
                        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-auto">
                          {cities
                            .filter(city => 
                              city.code.toLowerCase().includes(flight.to.toLowerCase()) ||
                              city.city.toLowerCase().includes(flight.to.toLowerCase()) ||
                              city.airport.toLowerCase().includes(flight.to.toLowerCase()) ||
                              city.keywords.some(keyword => keyword.toLowerCase().includes(flight.to.toLowerCase()))
                            )
                            .slice(0, 8)
                            .map((city) => (
                              <button
                                key={city.code}
                                type="button"
                                onClick={() => updateMultiCityFlight(flight.id, 'to', city.code)}
                                className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50"
                              >
                                <div className="font-medium">{city.code} - {city.airport}</div>
                                <div className="text-sm text-gray-500">{city.city}{city.state ? `, ${city.state}` : ''}, {city.country}</div>
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Depart */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Depart <span className="text-red-500">•</span>
                    </label>
                    <div className="relative max-w-[280px] focus-within:border-2 focus-within:border-[#0078d2] focus-within:p-[2px]">
                      <input
                        type="text"
                        value={flight.departInput}
                        onChange={(e) => updateMultiCityFlight(flight.id, 'departInput', e.target.value)}
                        placeholder="Depart"
                        className="w-full h-12 px-4 pr-12 text-base focus:outline-none border border-gray-600"
                      />
                      <button
                        type="button"
                        onClick={() => openDatePicker('depart')}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2"
                      >
                        <img 
                          src="/calender.png" 
                          alt="Calendar icon" 
                          className="w-8 h-7"
                        />
                      </button>
                    </div>
                  </div>
                </div>

                {/* Include nearby airports checkboxes */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1 mb-4">
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id={`include-nearby-from-${flight.id}`}
                      checked={flight.includeNearbyFrom}
                      onChange={(e) => updateMultiCityFlight(flight.id, 'includeNearbyFrom', e.target.checked)}
                      className="h-4 w-4 text-[#00467f] border-gray-600 rounded focus:ring-[#00467f]" 
                    />
                    <label htmlFor={`include-nearby-from-${flight.id}`} className="ml-2 text-sm text-gray-600">Include nearby airports</label>
                  </div>
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id={`include-nearby-to-${flight.id}`}
                      checked={flight.includeNearbyTo}
                      onChange={(e) => updateMultiCityFlight(flight.id, 'includeNearbyTo', e.target.checked)}
                      className="h-4 w-4 text-[#00467f] border-gray-600 rounded focus:ring-[#00467f]" 
                    />
                    <label htmlFor={`include-nearby-to-${flight.id}`} className="ml-2 text-sm text-gray-600">Include nearby airports</label>
                  </div>
                </div>
              </div>
            ))}
            
            {/* Add another flight button */}
            <button
              type="button"
              onClick={addFlight}
              className="text-[#0078d2] hover:underline flex items-center text-sm font-medium"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
              </svg>
              Add another flight
            </button>
          </div>
        ) : (
          /* Regular Round Trip/One Way Form */
          <>
            <div className="relative mb-6">
              <div className="flex items-start relative max-w-[800px]">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    From <span className="text-red-500">•</span>
                  </label>
                  <div className="relative max-w-[280px] focus-within:border-2 focus-within:border-[#0078d2] focus-within:p-[2px]" ref={fromDropdownRef}>
                    <input
                      type="text"
                      value={fromQuery}
                      onChange={(e) => {
                        setFromQuery(e.target.value);
                        setIsFromDropdownOpen(e.target.value.length > 0);
                      }}
                      placeholder="Leaving from"
                      role="combobox"
                      aria-label="Leaving from"
                      className={`w-full h-[45px] bg-white text-base focus:outline-none pl-4 pr-12 transition-all duration-200 ${
                        errors.includes("From") && !fromQuery ? 'border-red-500 border-2' : fromQuery ? 'border border-black' : 'border border-gray-600'
                      }`}
                    />
                    
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <img 
                        src="/arrow.png" 
                        alt="Arrow icon" 
                        className="w-12  h-12 brightness-50"
                      />
                    </div>
                 
                    {fromQuery && isFromDropdownOpen && (
                      <div className="absolute z-20 mt-1 w-[450px] bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-auto">
                        {cities
                          .filter(city => 
                            city.code.toLowerCase().includes(fromQuery.toLowerCase()) ||
                            city.city.toLowerCase().includes(fromQuery.toLowerCase()) ||
                            city.airport.toLowerCase().includes(fromQuery.toLowerCase()) ||
                            city.keywords.some(keyword => keyword.toLowerCase().includes(fromQuery.toLowerCase()))
                          )
                          .slice(0, 8)
                          .map((city, index) => (
                            <button
                              key={city.code}
                              type="button"
                              onClick={() => {
                                setFromQuery(city.code);
                                setIsFromDropdownOpen(false);
                              }}
                              className="w-full text-left px-4 py-3 text-gray-500 hover:bg-blue-50"
                            >
                              <div className="font-normal">{city.code} - {city.airport}</div>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>

                  <div className="mt-2 flex items-center">
                    <input type="checkbox" id="include-nearby-from" className="h-4 w-4 text-[#00467f] border-gray-600 rounded focus:ring-[#00467f]" />
                    <label htmlFor="include-nearby-from" className="ml-2 text-sm text-gray-600">Include nearby airports</label>
                  </div>

                  {/* Connecting Airport Field */}
                  {showConnecting && (
                    <div className="mt-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Connecting Airport <span className="text-red-500">•</span>
                      </label>
                      <div className="relative max-w-[280px] focus-within:border-2 focus-within:border-[#0078d2] focus-within:p-[2px]" ref={connectingDropdownRef}>
                        <input
                          type="text"
                          value={connectingQuery}
                          onChange={(e) => {
                            setConnectingQuery(e.target.value);
                            setIsConnectingDropdownOpen(e.target.value.length > 0);
                          }}
                          placeholder="Connecting airport/city"
                          role="combobox"
                          aria-label="Connecting airport/city"
                          className={`w-full h-[45px] bg-white text-base focus:outline-none px-4 ${
                            connectingQuery ? 'border border-black' : 'border border-gray-600'
                          }`}
                        />
                        
                        {connectingQuery && isConnectingDropdownOpen && (
                          <div className="absolute z-20 mt-1 w-[450px] bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-auto">
                            {cities
                              .filter(city => 
                                city.code.toLowerCase().includes(connectingQuery.toLowerCase()) ||
                                city.city.toLowerCase().includes(connectingQuery.toLowerCase()) ||
                                city.airport.toLowerCase().includes(connectingQuery.toLowerCase()) ||
                                city.keywords.some(keyword => keyword.toLowerCase().includes(connectingQuery.toLowerCase()))
                              )
                              .slice(0, 8)
                              .map((city, index) => (
                                <button
                                  key={city.code}
                                  type="button"
                                  onClick={() => {
                                    setConnectingQuery(city.code);
                                    setIsConnectingDropdownOpen(false);
                                  }}
                                  className="w-full text-left px-4 py-3 text-gray-500 hover:bg-blue-50"
                                >
                                  <div className="font-normal">{city.code} - {city.airport}</div>
                                </button>
                              ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Swap button positioned properly between from and to fields */}
                <div className="flex items-center mt-8 hidden md:flex mx-2">
                  <button 
                    type="button"
                    onClick={handleSwapAirports}
                    className="btn-swap bg-transparent hover:bg-gray-50 p-2 transition-all duration-200 focus:outline-none"
                    aria-label="Click to reverse the departure and arrival airports"
                  >
                    <div className="flex flex-col items-center">
                      <svg className="w-4 h-4 text-[#0078d2] mb-1" fill="currentColor" stroke="none" viewBox="0 0 24 24">
                        <path d="M13.025 1l-2.847 2.828 6.176 6.176h-16.354v3.992h16.354l-6.176 6.176 2.847 2.828 10.975-11z"/>
                      </svg>
                      <svg className="w-4 h-4 text-[#0078d2]" fill="currentColor" stroke="none" viewBox="0 0 24 24">
                        <path d="M10.975 23l2.847-2.828-6.176-6.176h16.354v-3.992h-16.354l6.176-6.176-2.847-2.828-10.975 11z"/>
                      </svg>
                    </div>
                  </button>
                </div>

                <div className="flex-1 ml-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    To <span className="text-red-500">•</span>
                  </label>
                  <div className="relative max-w-[280px] focus-within:border-2 focus-within:border-[#0078d2] focus-within:p-[2px]" ref={toDropdownRef}>
                    <input
                      type="text"
                      value={toQuery}
                      onChange={(e) => {
                        setToQuery(e.target.value);
                        setIsToDropdownOpen(e.target.value.length > 0);
                      }}
                      placeholder="Going to"
                      role="combobox"
                      aria-label="Going to"
                      className={`w-full h-[45px] bg-white text-base focus:outline-none px-4 ${
                        toQuery ? 'border border-black' : 'border border-gray-600'
                      }`}
                    />
                    
                    {toQuery && isToDropdownOpen && (
                      <div className="absolute z-20 mt-1 w-[450px] bg-white border border-gray-300 rounded-lg shadow-lg max-h-80 overflow-auto">
                        {cities
                          .filter(city => 
                            city.code.toLowerCase().includes(toQuery.toLowerCase()) ||
                            city.city.toLowerCase().includes(toQuery.toLowerCase()) ||
                            city.airport.toLowerCase().includes(toQuery.toLowerCase()) ||
                            city.keywords.some(keyword => keyword.toLowerCase().includes(toQuery.toLowerCase()))
                          )
                          .slice(0, 8)
                          .map((city, index) => (
                            <button
                              key={city.code}
                              type="button"
                              onClick={() => {
                                setToQuery(city.code);
                                setIsToDropdownOpen(false);
                              }}
                              className="w-full text-left px-4 py-3 text-gray-500 hover:bg-blue-50"
                            >
                              <div className="font-normal">{city.code} - {city.airport}</div>
                            </button>
                          ))}
                      </div>
                    )}
                  </div>
                  <div className="mt-2 flex items-center">
                    <input type="checkbox" id="include-nearby-to" className="h-4 w-4 text-[#00467f] border-gray-600 rounded focus:ring-[#00467f]" />
                    <label htmlFor="include-nearby-to" className="ml-2 text-sm text-gray-600">Include nearby airports</label>
                  </div>
                </div>

                {/* Mobile swap button */}
                <div className="flex justify-center my-2 md:hidden w-full">
                  <button 
                    type="button"
                    onClick={handleSwapAirports}
                    className="btn-swap bg-white hover:bg-gray-50 border border-gray-300 rounded-full p-2 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-[#0078d2] focus:ring-opacity-50"
                    aria-label="Click to reverse the departure and arrival airports"
                  >
                    <span aria-hidden="true" className="icon-arrow-right block w-3 h-3 mb-1">
                      <svg className="w-3 h-3 text-[#0078d2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                      </svg>
                    </span>
                    <span aria-hidden="true" className="icon-arrow-left block w-3 h-3">
                      <svg className="w-3 h-3 text-[#0078d2]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16l-4-4m0 0l4-4m-4 4h18"></path>
                      </svg>
                    </span>
                  </button>
                </div>

                {/* Add connecting airport checkbox as third column */}
                <div className="hidden md:flex flex-col justify-end ml-8">
                  <div className="flex items-center mt-8">
                    <input 
                      type="checkbox" 
                      id="add-connecting" 
                      checked={showConnecting}
                      onChange={(e) => setShowConnecting(e.target.checked)}
                      className="h-4 w-4 text-[#00467f] border-gray-600 rounded focus:ring-[#00467f]" 
                    />
                    <label htmlFor="add-connecting" className="ml-2 text-sm text-gray-600">Add connecting airport/city</label>
                  </div>
                </div>

                {/* Mobile connecting airport checkbox */}
                <div className="md:hidden w-full mt-4">
                  <div className="flex items-center">
                    <input 
                      type="checkbox" 
                      id="add-connecting-mobile" 
                      checked={showConnecting}
                      onChange={(e) => setShowConnecting(e.target.checked)}
                      className="h-4 w-4 text-[#00467f] border-gray-600 rounded focus:ring-[#00467f]" 
                    />
                    <label htmlFor="add-connecting-mobile" className="ml-2 text-sm text-gray-600">Add connecting airport/city</label>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-start relative max-w-[600px] mb-6">
              <div className="flex-1 ">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Depart <span className="text-red-500">•</span>
                </label>
                <div className="relative max-w-[280px] focus-within:border-2 focus-within:border-[#0078d2] focus-within:p-[2px] mr-10">
                  <input
                    type="text"
                    value={departInput}
                    onChange={(e) => setDepartInput(e.target.value)}
                    placeholder="Depart"
                    className={`w-full h-[45px] px-4 text-base focus:outline-none ${
                      departInput ? 'border border-black' : 'border border-gray-600'
                    } ${departInput ? 'text-gray-900' : 'text-gray-500'}`}
                  />
                  <button
                    type="button"
                    onClick={() => openDatePicker('depart')}
                    className="rounded items-center justify-center appearance-none box-border cursor-pointer flex h-9 w-9 p-0 -mt-px focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-blue-070 disabled:pointer-events-none text-blue-060 bg-transparent absolute right-2 top-1/2 transform -translate-y-1/2"
                    aria-label="Depart"
                  >
                    <img 
                      src="/calender.png" 
                      alt="Calendar icon" 
                      className="w-12 h-10"
                    />
                    <span className="sr-only">Depart</span>
                  </button>
                </div>
              </div>

              {tripType !== "oneWay" && (
                <div className="flex-1 ml-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Return <span className="text-red-500">•</span>
                  </label>
                  <div className="relative max-w-[280px] focus-within:border-2 focus-within:border-[#0078d2] focus-within:p-[2px]">
                    <input
                      type="text"
                      value={returnInput}
                      onChange={(e) => setReturnInput(e.target.value)}
                      placeholder="Return"
                      className={`w-full h-[45px] px-4 pr-12 text-base focus:outline-none ${
                        returnInput ? 'border border-black' : 'border border-gray-600'
                      } ${returnInput ? 'text-gray-900' : 'text-gray-500'}`}
                    />
                    <button
                      type="button"
                      onClick={() => openDatePicker('return')}
                      className="rounded items-center justify-center appearance-none box-border cursor-pointer flex h-9 w-9 p-0 -mt-px focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-blue-070 disabled:pointer-events-none text-blue-060 bg-transparent disabled:text-neutral-090 absolute right-2 top-1/2 transform -translate-y-1/2"
                      aria-label="Return"
                    >
                      <img 
                        src="/calender.png" 
                        alt="Calendar icon" 
                         className="w-12 h-10"
                      />
                      <span className="sr-only">Return</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Redeem miles section */}
        <div className="mb-6">
          <div className="flex items-center">
            <input 
              type="checkbox" 
              id="redeem-miles" 
              checked={redeemMiles}
              onChange={(e) => setRedeemMiles(e.target.checked)}
              className="h-4 w-4 text-[#00467f] border-gray-600 rounded focus:ring-[#00467f]" 
            />
            <label htmlFor="redeem-miles" className="ml-2 text-sm text-gray-800">Redeem miles</label>
          </div>
          <div className="mt-2">
            <a href="/aadvantage" className="text-[#0078d2] hover:underline text-sm">
              Log in to your AAdvantage account »
            </a>
          </div>
        </div>

        
      </div>
       {/* Divider */}
       <div className="my-8 border-t border-gray-200"></div>

       {/* Passengers Section */}
       <div className="mb-8">
         <h2 className="text-3xl font-light text-blue-500 mb-8">Passengers</h2>
         
         <div className="grid grid-cols-1 lg:grid-cols-4 gap-8 mb-6">
           {/* Left Side - Number of passengers */}
           <div className="lg:col-span-1">
             <label className="block text-sm font-medium text-gray-700 mb-2">
               Number of passengers
             </label>
             <div className="relative max-w-xs focus-within:border-2 focus-within:border-[#0078d2] focus-within:p-[2px] focus-within:rounded-[4px]">
               <select
                 value={adults}
                 onChange={(e) => setAdults(parseInt(e.target.value))}
                 className="w-full h-12 px-4 pr-12 border border-[#0078d2] bg-white appearance-none text-base focus:outline-none rounded-[4px]"
               >
                 {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                   <option key={num} value={num}>
                     {num}
                   </option>
                 ))}
               </select>
               <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                 <svg className="h-4 w-4 text-[#0078d2]" fill="currentColor" viewBox="0 0 12 12">
                   <path d="M6 8L2 4h8L6 8z" />
                 </svg>
               </div>
             </div>
           </div>

           {/* Right Side - Passenger fields */}
           <div className="lg:col-span-3">
             <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
               {Array.from({ length: adults }, (_, index) => (
                 <div key={index}>
                   <label className="block text-sm font-medium text-gray-700 mb-2">
                     Passenger {index + 1}
                   </label>
                   <div className="relative focus-within:border-2 focus-within:border-[#0078d2] focus-within:p-[2px] focus-within:rounded-[4px]">
                     <select
                       className="w-full h-12 px-4 pr-12 border border-gray-600 bg-white appearance-none text-base focus:outline-none rounded-[4px]"
                     >
                       <option value="adult">Adult (16-64)</option>
                       <option value="senior">Senior (65+)</option>
                       <option value="youth">Youth adult (12-15)</option>
                       <option value="child">Child (2-11)</option>
                       <option value="infant">Infant in seat (under 2)</option>
                     </select>
                     <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                       <svg className="h-4 w-4 text-[#0078d2]" fill="currentColor" viewBox="0 0 12 12">
                         <path d="M6 8L2 4h8L6 8z" />
                       </svg>
                     </div>
                   </div>
                 </div>
               ))}
             </div>
           </div>
         </div>

         <div className="space-y-3 text-sm">
           <a href="#" className="text-[#0078d2] hover:underline flex items-center">
             Unaccompanied minors
             <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
             </svg>
           </a>
           <a href="#" className="text-[#0078d2] hover:underline flex items-center">
             Traveling with infants
             <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
             </svg>
           </a>
           <a href="#" className="text-[#0078d2] hover:underline flex items-center">
             For groups of 10 or more, contact Group & Meeting Travel
             <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
               <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"></path>
             </svg>
           </a>
         </div>
       </div>

       {/* add a divider */}
       <div className="my-8 border-t border-gray-200"></div>

       {/* Options Section */}
       <div className="mb-8">
         <h2 className="text-3xl font-light text-blue-500 mb-8">Options</h2>
         
         <div className="grid grid-cols-1 md:grid-cols-3 grid-rows-2 gap-6">
           {/* Row 1, Column 1 - Class */}
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">
               Class
             </label>
             <div className="relative max-w-xs focus-within:border-2 focus-within:border-[#0078d2] focus-within:p-[2px]">
               <select
                 className="w-full h-12 px-4 pr-12 border border-gray-600 bg-white appearance-none text-base focus:outline-none rounded-none"
               >
                 <option value="all">Show all</option>
                 <option value="first">First</option>
                 <option value="business">Business</option>
                 <option value="premium">Premium Economy</option>
                 <option value="economy">Economy</option>
               </select>
               <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                 <svg className="h-4 w-4 text-[#0078d2]" fill="currentColor" viewBox="0 0 12 12">
                   <path d="M6 8L2 4h8L6 8z" />
                 </svg>
               </div>
             </div>
           </div>

           {/* Row 1, Column 2 - Airline */}
           <div>
             <label className="block text-sm font-medium text-gray-700 mb-2">
               Airline
             </label>
             <div className="relative max-w-xs focus-within:border-2 focus-within:border-[#0078d2] focus-within:p-[2px]">
               <select
                 className="w-full h-12 px-4 pr-12 border border-gray-600 bg-white appearance-none text-base focus:outline-none rounded-none"
               >
                 <option value="all">All airlines</option>
                 <option value="american">American Airlines</option>
               </select>
               <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                 <svg className="h-4 w-4 text-[#0078d2]" fill="currentColor" viewBox="0 0 12 12">
                   <path d="M6 8L2 4h8L6 8z" />
                 </svg>
               </div>
             </div>
           </div>

           {/* Row 1, Column 3 - Empty */}
           <div></div>

           {/* Single continuous border line between rows */}
           <div className="col-span-3 border-t border-gray-200 my-6"></div>

           {/* Row 2, Column 1 - Empty */}
           <div></div>

           {/* Row 2, Column 2 - Empty */}
           <div></div>

           {/* Row 2, Column 3 - Search Button */}
           <div className="flex items-end">
             <button 
               onClick={handleSearch}
               className="w-full bg-blue-600 text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-blue-700 transition-colors h-12 ml-10"
             >
               Search
             </button>
           </div>
         </div>
       </div>
      </div>
//divider

    {/* Date Picker Modal */}
    {isDatePickerOpen && (
      <div className="fixed inset-0 bg-black bg-opacity-85 flex items-center justify-center z-50">
        <div ref={datePickerRef} className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-8 p-6">
          {/* Header - only show for round trip */}
          {tripType === "roundTrip" && (
            <div className="pb-2 ">
              <div className="grid grid-cols-2 gap-8 border-b border-gray-400 ">
                <div className="text-center">
                  <div className=" font-semibold text-gray-900">Depart</div>
                  <div className="text-base text-gray-600">
                    {departDate ? formatDate(departDate) : 'Fri, Aug 22, 2025'}
                  </div>
                </div>
                <div className="text-center">
                  <div className="font-semibold text-gray-900 ">Return</div>
                  <div className="text-base text-gray-600">
                    {returnDate ? formatDate(returnDate) : 'Fri, Aug 29, 2025'}
                  </div>
                </div>
              </div>
              {/* Selection indicator */}
              <div className="grid grid-cols-2 gap-8 -mt-0.5">
                <div className={`rounded-full ${activeDateField === 'depart' ? 'h-1 bg-blue-500' : ''}`}></div>
                <div className={`rounded-full ${activeDateField === 'return' ? 'h-1 bg-blue-500' : ''}`}></div>
              </div>
            </div>
          )}

          {/* Calendar */}
          <div className={`p-3 ${tripType === "roundTrip" ? "pt-0" : ""}`}>
            <div className="flex justify-between items-center mb-6">
              <button
                type="button"
                onClick={prevMonth}
                disabled={isPrevMonthDisabled()}
                className={`p-1 rounded-full border ${
                  isPrevMonthDisabled() 
                    ? 'border-gray-300 text-gray-300 cursor-not-allowed' 
                    : 'border-blue-500 text-blue-500 hover:bg-blue-50'
                }`}
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              
              <div className="grid grid-cols-2 gap-6 flex-1">
                <div className="text-xl font-semibold text-gray-900 text-center">
                  {currentMonth.toLocaleDateString('en-US', { month: 'long' })}
                </div>
                <div className="text-xl font-semibold text-gray-900 text-center">
                  {getNextMonth(currentMonth).toLocaleDateString('en-US', { month: 'long' })}
                </div>
              </div>
              
              <button
                type="button"
                onClick={nextMonth}
                className="p-2 hover:bg-blue-50 rounded-full border border-blue-500 text-blue-500"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>

            {/* Two Month Calendar Grid */}
            <div className="grid grid-cols-2 gap-6">
              {/* First Month */}
              <div>
                <div className="grid grid-cols-7 gap-1 mb-4">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                    <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                  {getDaysInMonth(currentMonth).map((date, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => date && handleDateSelect(date)}
                      disabled={!date || date < new Date()}
                      className={`
                        p-2 text-sm transition-colors w-10 h-10 rounded-lg border-2
                        ${!date ? 'invisible' : ''}
                        ${date && date < new Date() ? 'text-gray-300 cursor-pointer border-transparent' : 'text-gray-900 hover:border-gray-300 border-transparent'}
                        ${isSameDay(date, departDate) ? 'bg-aa-dark-blue text-white hover:bg-aa-dark-blue/90 border-aa-dark-blue' : ''}
                        ${tripType === "roundTrip" && isSameDay(date, returnDate) ? 'bg-aa-dark-blue text-white hover:bg-aa-dark-blue/90 border-aa-dark-blue' : ''}
                        ${tripType === "roundTrip" && isInRange(date, departDate, returnDate) && !isSameDay(date, departDate) && !isSameDay(date, returnDate) ? 'bg-blue-100 border-transparent' : ''}
                      `}
                    >
                      {date ? date.getDate() : ''}
                    </button>
                  ))}
                </div>
              </div>

              {/* Second Month */}
              <div>
                <div className="grid grid-cols-7 gap-1 mb-4">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map(day => (
                    <div key={day} className="text-center text-sm font-medium text-gray-500 py-2">
                      {day}
                    </div>
                  ))}
                  {getDaysInMonth(getNextMonth(currentMonth)).map((date, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => date && handleDateSelect(date)}
                      disabled={!date || date < new Date()}
                      className={`
                        p-2 text-sm transition-colors w-10 h-10 rounded-lg border-2
                        ${!date ? 'invisible' : ''}
                        ${date && date < new Date() ? 'text-gray-300 cursor-pointer border-transparent' : 'text-gray-900 hover:border-gray-300 border-transparent'}
                        ${isSameDay(date, departDate) ? 'bg-aa-dark-blue text-white hover:bg-aa-dark-blue/90 border-aa-dark-blue' : ''}
                        ${tripType === "roundTrip" && isSameDay(date, returnDate) ? 'bg-aa-dark-blue text-white hover:bg-aa-dark-blue/90 border-aa-dark-blue' : ''}
                        ${tripType === "roundTrip" && isInRange(date, departDate, returnDate) && !isSameDay(date, departDate) && !isSameDay(date, returnDate) ? 'bg-blue-100 border-transparent' : ''}
                      `}
                    >
                      {date ? date.getDate() : ''}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-between items-center p-3 border-t border-gray-200">
            <button
              type="button"
              onClick={clearDates}
              className="text-aa-dark-blue hover:underline font-medium"
            >
              Clear dates
            </button>
            <button
              type="button"
              onClick={() => setIsDatePickerOpen(false)}
              className="bg-aa-dark-blue text-white px-8 py-3 rounded-lg hover:bg-aa-dark-blue/90 font-medium"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

// Reusable autocomplete for airport/city selection
const AutocompleteInput = ({
  value,
  onChange,
  onSelect,
  placeholder = "City or airport",
  hasError = false
}) => {
  const containerRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);

  const filtered = useMemo(() => {
    const q = (value || "").trim().toLowerCase();
    if (!q) return [];
    return cities
      .filter((c) => {
        const inKeywords = (c.keywords || []).some((k) =>
          k.toLowerCase().includes(q)
        );
        const inText =
          `${c.city} ${c.airport} ${c.state} ${c.country} ${c.code}`
            .toLowerCase()
            .includes(q);
        return inKeywords || inText;
      })
      .slice(0, 12);
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!containerRef.current) return;
      if (!containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  return (
    <div className="relative focus-within:border-2 focus-within:border-[#00467f] focus-within:p-[2px]" ref={containerRef}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setIsOpen(true)}
        placeholder={placeholder}
        className={`w-full h-10 px-3 border placeholder-gray-500 text-sm focus:outline-none ${
          hasError ? 'border-red-500 border-2' : 'border-gray-600'
        }`}
      />
      {isOpen && filtered.length > 0 && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-600 rounded-sm shadow max-h-80 overflow-auto">
          {filtered.map((opt, idx) => {
            const isActive = idx === activeIndex;
            return (
              <button
                key={`${opt.code}-${opt.city}`}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onMouseEnter={() => setActiveIndex(idx)}
                onMouseLeave={() => setActiveIndex(-1)}
                onClick={() => {
                  onSelect(opt);
                  setIsOpen(false);
                }}
                className={`block w-full text-left px-3 py-2 hover:bg-[#00467f] ${
                  isActive ? "bg-[#00467f]" : "bg-white"
                }`}
              >
                <div
                  className={`text-sm font-semibold whitespace-nowrap truncate leading-5 ${
                    isActive ? "text-white" : "text-[#00467f] hover:text-white"
                  }`}
                >
                  {opt.display}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ValidationFlightSearch = ({
  tripType,
  setTripType,
  redeemMiles,
  setRedeemMiles,
  adults,
  setAdults,
  children,
  setChildren,
  activeService,
  fromQuery,
  setFromQuery,
  toQuery,
  setToQuery,
  departDate,
  setDepartDate,
  returnDate,
  setReturnDate,
  departInput,
  setDepartInput,
  returnInput,
  setReturnInput,
  farePreference,
  setFarePreference,
  errors
}) => {
  const navigate = useNavigate();

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = [];

    if (!toQuery.trim()) newErrors.push("To");
    if (!departInput.trim()) newErrors.push("Depart");
    if (tripType === "roundTrip" && !returnInput.trim()) newErrors.push("Return");

    if (newErrors.length > 0) {
      // Stay on validation page with updated errors
      navigate('/validation', {
        state: {
          errors: newErrors,
          formData: {
            tripType,
            redeemMiles,
            adults,
            children,
            fromQuery,
            toQuery,
            departDate,
            returnDate,
            departInput,
            returnInput,
            farePreference
          }
        },
        replace: true
      });
    } else {
      // Navigate to search results
      navigate('/search-results');
    }
  };

  return (
    <div>
      {/* Trip Type and Redeem Miles */}
      <div className="flex flex-wrap items-center mb-4 gap-8">
        <div className="flex items-center space-x-8">
          <div className="flex items-center space-x-8">
            <label className="flex items-center space-x-3 text-base text-gray-800">
              <input
                type="radio"
                name="tripType"
                value="roundTrip"
                checked={tripType === "roundTrip"}
                onChange={(e) => setTripType(e.target.value)}
                className="h-5 w-5 accent-[#00467f] border-2 border-gray-600 focus:ring-0"
              />
              <span className="font-medium">Round trip</span>
            </label>
            <label className="flex items-center space-x-3 text-base text-gray-800">
              <input
                type="radio"
                name="tripType"
                value="oneWay"
                checked={tripType === "oneWay"}
                onChange={(e) => setTripType(e.target.value)}
                className="h-5 w-5 accent-[#00467f] border-2 border-gray-600 focus:ring-0"
              />
              <span className="font-medium">One way</span>
            </label>
          </div>
        </div>

        <div className="flex items-center">
          <label className="flex items-center space-x-3 text-base text-gray-800">
            <input
              type="checkbox"
              checked={redeemMiles}
              onChange={(e) => setRedeemMiles(e.target.checked)}
              className="h-5 w-5 accent-[#00467f] rounded-[3px] border-2 border-gray-600 focus:ring-0"
            />
            <span className="font-medium">Redeem miles</span>
          </label>
        </div>
      </div>

      <div className="mb-4">
        <p className="text-[#00467f] text-sm">Log in to your AAdvantage account »</p>
      </div>

      {/* Flight Search Form */}
      <form className="space-y-3" onSubmit={handleSubmit}>
        {/* Row 1: From, To, Number of passengers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From •
            </label>
            <div className="relative">
              <AutocompleteInput
                value={fromQuery}
                onChange={setFromQuery}
                onSelect={(opt) => setFromQuery(opt.code)}
                placeholder="Going to"
                hasError={errors.includes("From") && !fromQuery}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  className="h-4 w-4 text-[#00467f] rotate-90"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <input type="checkbox" id="include-nearby-from" className="h-4 w-4 text-[#00467f] border-gray-600 rounded" />
              <label htmlFor="include-nearby-from" className="ml-2 text-sm text-gray-600">Include nearby airports</label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To •
            </label>
            <div className="relative">
              <AutocompleteInput
                value={toQuery}
                onChange={setToQuery}
                onSelect={(opt) => setToQuery(opt.code)}
                placeholder="Going to"
                hasError={errors.includes("To") && !toQuery}
              />
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  className="h-4 w-4 text-[#00467f] rotate-90"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
            </div>
            <div className="mt-2 flex items-center">
              <input type="checkbox" id="include-nearby-to" className="h-4 w-4 text-[#00467f] border-gray-600 rounded" />
              <label htmlFor="include-nearby-to" className="ml-2 text-sm text-gray-600">Include nearby airports</label>
            </div>
            <div className="mt-2">
              <input type="checkbox" id="add-connecting" className="h-4 w-4 text-[#00467f] border-gray-600 rounded" />
              <label htmlFor="add-connecting" className="ml-2 text-sm text-gray-600">Add connecting airport/city</label>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Number of passengers
            </label>
            <div className="relative focus-within:border-2 focus-within:border-[#00467f] focus-within:p-[2px]">
              <select
                value={adults}
                onChange={(e) => setAdults(parseInt(e.target.value))}
                className="w-full h-10 px-3 pr-12 border border-gray-600 bg-white appearance-none text-base focus:outline-none rounded-none"
              >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
                  <option key={num} value={num}>
                    {num}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                <svg
                  className="h-4 w-5 text-[#00467f]"
                  fill="currentColor"
                  viewBox="0 0 12 12"
                  aria-hidden="true"
                >
                  <path d="M6 8L2 4h8L6 8z" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Dates */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Depart •
            </label>
            <div className="relative focus-within:border-2 focus-within:border-[#00467f] focus-within:p-[2px]">
              <input
                type="text"
                value={departInput}
                onChange={(e) => setDepartInput(e.target.value)}
                placeholder="Depart"
                className={`w-full h-10 px-3 pr-8 placeholder-gray-500 text-sm focus:outline-none ${
                  errors.includes("Depart") && !departInput ? 'border-red-500 border-2' : 'border border-gray-600'
                }`}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 flex items-center pr-3"
              >
                <svg
                  className="h-4 w-4 text-[#0078d2]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v12a2 2 0 002 2z"
                  />
                </svg>
              </button>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Return •
            </label>
            <div className="relative focus-within:border-2 focus-within:border-[#00467f] focus-within:p-[2px]">
              <input
                type="text"
                value={returnInput}
                onChange={(e) => setReturnInput(e.target.value)}
                placeholder="Return"
                disabled={tripType === "oneWay"}
                className={`w-full h-10 px-3 pr-8 placeholder-gray-500 text-sm focus:outline-none disabled:bg-gray-100 disabled:cursor-not-allowed ${
                  errors.includes("Return") && !returnInput ? 'border-red-500 border-2' : 'border border-gray-600'
                }`}
              />
              <button
                type="button"
                disabled={tripType === "oneWay"}
                className="absolute inset-y-0 right-0 flex items-center pr-3 disabled:cursor-not-allowed"
              >
                <svg
                  className="h-4 w-4 text-[#0078d2]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 002 2v12a2 2 0 002 2z"
                  />
                </svg>
              </button>
            </div>
          </div>
         
        </div>
        <div className="flex items-end">
            <button
              type="submit"
              className="w-full h-10 bg-[#00467f] text-white px-5 rounded-sm hover:bg-[#003354] transition-colors font-medium text-base"
            >
              Search
            </button>
          </div>
      </form>
    </div>
  );
};

export default ValidationPage;